import { cn } from '@ifixit/ui/lib/utils';
import type { ComponentProps } from 'react';

function Skeleton({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="skeleton"
         className={cn(
            'animate-pulse rounded-md bg-gray-200 motion-reduce:animate-none',
            className
         )}
         {...props}
      />
   );
}

interface SkeletonTextProps extends ComponentProps<'div'> {
   /**
    * Number of skeleton lines to render
    * @default 3
    */
   noOfLines?: number;
   /**
    * Gap between skeleton lines
    * @default '0.5rem'
    */
   spacing?: string;
   /**
    * Height of each skeleton line
    * @default '1rem'
    */
   skeletonHeight?: string | number;
   /**
    * Whether the content has loaded (if true, renders children instead)
    * @default false
    */
   isLoaded?: boolean;
}

function SkeletonText({
   className,
   noOfLines = 3,
   spacing = '0.5rem',
   skeletonHeight = '1rem',
   isLoaded = false,
   children,
   ...props
}: SkeletonTextProps) {
   if (isLoaded) {
      return <>{children}</>;
   }

   const lines = Array.from({ length: noOfLines }, (_, i) => i);
   const height = typeof skeletonHeight === 'number' ? `${skeletonHeight}px` : skeletonHeight;

   return (
      <div
         data-slot="skeleton-text"
         className={cn('flex flex-col', className)}
         style={{ gap: spacing }}
         {...props}
      >
         {lines.map(i => (
            <Skeleton
               key={i}
               style={{ height }}
               className={i === lines.length - 1 ? 'w-4/5' : 'w-full'}
            />
         ))}
      </div>
   );
}

interface SkeletonCircleProps extends ComponentProps<'div'> {
   /**
    * Size of the circle
    * @default '2.5rem'
    */
   size?: string | number;
}

function SkeletonCircle({ className, size = '2.5rem', ...props }: SkeletonCircleProps) {
   const sizeValue = typeof size === 'number' ? `${size}px` : size;

   return (
      <Skeleton
         data-slot="skeleton-circle"
         className={cn('rounded-full shrink-0', className)}
         style={{ width: sizeValue, height: sizeValue }}
         {...props}
      />
   );
}

export { Skeleton, SkeletonText, SkeletonCircle };
